import React, { createContext, useState, useContext, useEffect } from "react";
import iro from "@jaames/iro"; // Import iro.js

// Define the context for the theme
const ThemeContext = createContext();

// Default theme setup (will be used when no theme is saved in localStorage)
const defaultTheme = {
  background: "#ffffff",
  text: "#000000",
  button: "#E5D8CF",
  table: "#E5D8CF", // Define the default table color
};

export const useTheme = () => {
  return useContext(ThemeContext);
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme); // Default theme
  const [showSettings, setShowSettings] = useState(false); // State to toggle settings panel visibility

  // Initialize iro.js color pickers for background, text, and button colors
  const [backgroundPicker, setBackgroundPicker] = useState(null);
  const [textPicker, setTextPicker] = useState(null);
  const [buttonPicker, setButtonPicker] = useState(null);
  const [tablePicker, setTablePicker] = useState(null);

  // Load theme from localStorage on initial load
  useEffect(() => {
    const savedTheme = JSON.parse(localStorage.getItem("theme"));
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  // This effect initializes the color pickers, but only when the settings panel is visible
  useEffect(() => {
    if (!showSettings) return; // Do nothing if the settings are not visible

    // Initialize the color pickers when the settings are visible
    const bgPicker = new iro.ColorPicker("#backgroundPicker", {
      width: 200,
      color: theme.background, // Use the current background color
      borderWidth: 1,
      borderColor: "#ccc",
    });

    const txtPicker = new iro.ColorPicker("#textPicker", {
      width: 200,
      color: theme.text, // Use the current text color
      borderWidth: 1,
      borderColor: "#ccc",
    });

    const btnPicker = new iro.ColorPicker("#buttonPicker", {
      width: 200,
      color: theme.button, // Use the current button color
      borderWidth: 1,
      borderColor: "#ccc",
    });

    const tablePicker = new iro.ColorPicker("#tablePicker", {
      width: 200,
      color: theme.table, // Use the current table color
      borderWidth: 1,
      borderColor: "#ccc",
    });

    // Update theme when colors change
    bgPicker.on("color:change", (color) => {
      const updatedTheme = {
        ...theme,
        background: color.hexString,
      };
      setTheme(updatedTheme);
      localStorage.setItem("theme", JSON.stringify(updatedTheme)); // Save to localStorage
    });

    txtPicker.on("color:change", (color) => {
      const updatedTheme = {
        ...theme,
        text: color.hexString,
      };
      setTheme(updatedTheme);
      localStorage.setItem("theme", JSON.stringify(updatedTheme)); // Save to localStorage
    });

    btnPicker.on("color:change", (color) => {
      const updatedTheme = {
        ...theme,
        button: color.hexString,
      };
      setTheme(updatedTheme);
      localStorage.setItem("theme", JSON.stringify(updatedTheme)); // Save to localStorage
    });
    tablePicker.on("color:change", (color) => {
      const updatedTheme = {
        ...theme,
        table: color.hexString, // Set the table color here
      };
      setTheme(updatedTheme);
      localStorage.setItem("theme", JSON.stringify(updatedTheme)); // Save to localStorage
      console.log("Updated Theme:", updatedTheme);
    });

    // Save the color pickers to state for later updates
    setBackgroundPicker(bgPicker);
    setTextPicker(txtPicker);
    setButtonPicker(btnPicker);
    setTablePicker(tablePicker);
    // Clean up pickers when the settings are hidden or component unmounts
    return () => {
      bgPicker.destroy();
      txtPicker.destroy();
      btnPicker.destroy();
      tablePicker.destroy();
    };
  }, [showSettings, theme]); // Re-run effect when `showSettings` or `theme` changes

  // Function to toggle settings visibility
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme: setTheme }}>
      {children}

      {/* Conditionally render the settings panel */}
      {showSettings && (
        <div
          style={{
            padding: "20px",
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
            marginTop: "20px",
          }}
        >
          <h3>Pick your colors:</h3>

          <div style={{ marginBottom: "10px" }}>
            <h4 style={{ color: theme.button }}>Table Color:</h4>
            <div id="tablePicker"></div>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <h4 style={{ color: theme.background }}>Background Color:</h4>
            <div id="backgroundPicker"></div>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <h4 style={{ color: theme.text }}>Text Color:</h4>
            <div id="textPicker"></div>
          </div>

          <div style={{ marginBottom: "10px" }}>
            <h4 style={{ color: theme.button }}>Button Color:</h4>
            <div id="buttonPicker"></div>
          </div>
        </div>
      )}
    </ThemeContext.Provider>
  );
};
